import React, { useEffect, useState } from "react";
import {
  Grid,
  Button,
  Typography,
  Modal,
  TextField,
  Switch,
  FormGroup,
  FormControl,
  FormControlLabel,
  Collapse,
  MenuItem,
  IconButton,
  InputAdornment,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SelectWithClear from "../Form/SelectWithClear";
import { Link } from "@mui/icons-material";

import {} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { CURRENCYCODE } from "utils/options/CURRENCYCODE";
import { DEFAULT_SERVICE } from "utils/options/DEFAULT_SERVICE";
import { formatPrice } from "utils/priceformatter";

import { checkInvoiceNumberExists, createInvoice } from "API/swesum";
import ArticlesTable from "Pages/AdminInterface/invoices/components/ArticlesTable";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import DateWithClear from "../Form/DateWithClear";
import dayjs from "dayjs";
import { useAuth } from "Hooks/useAuth";
import { createMyInvoice } from "API/myPages";

const style = {
  alignItems: "center",
  padding: 5,
  width: "60%",
  position: "absolute",
  backgroundColor: "white",
  borderRadius: 3,
  maxWidth: "1000px",
  maxHeight: "700px",
  overflow: "auto",
  top: "25%",
  left: "25%",
};

export default function CreateInvoiceModal({
  handleClose,
  isOpen,
  client = null,
  endCustomer = null,
  endCustomers = [],
}) {
  const initialInvoiceData = {
    endCustomerId: endCustomer ? endCustomer.Id : null,
    invoiceNo: null,
    amount: null,
    currencyCode: "SEK",
    service: !!client && !endCustomer ? client.DefaultService : null,
    OCR: null,
    externalReference: null,
    ourReference: null,
    orderNo: null,
    invoiceDate: null,
    dueDate: null,
  };

  const { isAdmin } = useAuth();

  const [isCredit, setIsCredit] = useState(false);
  const [allowPrint, setAllowPrint] = useState(false);
  const [lines, setLines] = useState([]);
  const [lineCalcAmount, setLineCalcAmount] = useState(0);
  const [lineVatAmount, setLineVatAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [externalInvoiceNoErr, setExternalInvoiceNoErr] = useState("");
  const [externalCheckLoading, setExternalCheckLoading] = useState(false);

  const [invoiceData, setInvoiceData] = useState(initialInvoiceData);

  const asyncCheckExternalInvoiceNo = async () => {
    setExternalCheckLoading(true);
    if (!!invoiceData.invoiceNo) {
      const exists = await checkInvoiceNumberExists(
        invoiceData.invoiceNo,
        client.Id
      );
      if (exists) {
        setExternalInvoiceNoErr("Externt fakturanr finns redan");
      }
    }
    setExternalCheckLoading(false);
  };

  const { openError, openSuccess } = usePopupNotificationContext();

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    //if endcustomer changes, set selected service
    if (name === "endCustomerId") {
      const selectedEndCustomer = endCustomers.find(
        (e) => e.Id === Number(value)
      );

      const defaultService =
        selectedEndCustomer &&
        selectedEndCustomer.SameAgreementAsClient === "0" &&
        selectedEndCustomer.DefaultService
          ? selectedEndCustomer.DefaultService
          : client?.DefaultService || null;

      setInvoiceData((prevData) => ({
        ...prevData,
        ["service"]: defaultService,
      }));
    }

    setInvoiceData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCreate = async () => {
    setLoading(true);

    if (externalInvoiceNoErr) {
      openError("Externt fakturanr finns redan");
      setLoading(false);
      return;
    }

    const body = {
      EndCustomerId: invoiceData.endCustomerId,
      ClientId: client?.Id,
      ClientName: client?.Name,
      ExternalInvoiceNo: invoiceData.invoiceNo,
      Price: allowPrint ? lineCalcAmount : invoiceData.amount,
      ExcludePrinting: allowPrint ? Number(0) : Number(1),
      Service: invoiceData.service,
      OCR: invoiceData.OCR,
      ExternalReference: invoiceData.externalReference,
      OurReference: invoiceData.ourReference,
      OrderNo: invoiceData.orderNo,
      InvoiceDate: invoiceData.invoiceDate,
      OriginalFfdDate: invoiceData.dueDate,
      Lines: JSON.stringify(lines),
      Type: isCredit ? "Credit" : "Invoice",
      TotalVat: lineVatAmount,
    };

    try {
      if (isAdmin) {
        await createInvoice(body);
      } else {
        await createMyInvoice(body);
      }
      openSuccess("Faktura skapades");
      handleClose();
    } catch (error) {
      openError("Någonting gick fel, " + error.response.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setIsCredit(false);
    setAllowPrint(false);
    setInvoiceData(initialInvoiceData);
    setLineCalcAmount(0);
  }, [isOpen]);

  const getLineAmountExclVAT = (item) => {
    return Number(item.Quantity || 0) * Number(item.UnitPrice || 0);
  };

  useEffect(() => {
    if (lines.length > 0) {
      const newSum = lines.reduce(
        (partialSum, line) =>
          partialSum +
          Number(line.LineAmountExclVAT || 0) *
            (Number(line.VATPct || 0) / 100 + 1),
        0
      );

      setLineCalcAmount(formatPrice(newSum));
    }

    const VAT = lines.reduce(
      (partialSum, item) =>
        partialSum +
        (Number(getLineAmountExclVAT(item) || 0) * Number(item.VATPct || 0)) /
          100,
      0
    );

    setLineVatAmount(formatPrice(VAT));
  }, [lines]);

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Grid item container sx={style}>
        <Grid item container>
          <Grid item md={4}>
            <Typography
              color="black"
              variant="h5"
              fontWeight="bold"
              align="left"
              fullWidth
            >
              Skapa faktura
            </Typography>
          </Grid>
          <Grid item container md={12}>
            <Grid item container alignItems={"center"} md={6}>
              <IconButton
                sx={{ marginLeft: -1.3 }}
                onClick={() => navigate(`/clients/${client.Id}`)}
              >
                <Link sx={{ color: "#1976d2" }} />
              </IconButton>

              <Typography fontWeight={"bold"} align="left">
                {client?.Name}
              </Typography>
            </Grid>
            <Grid item md={6}>
              <FormGroup
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 3,
                  width: "100%",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch onChange={(e) => setIsCredit(e.target.checked)} />
                  }
                  label="Kredit"
                />
                <FormControlLabel
                  control={
                    <Switch onChange={(e) => setAllowPrint(e.target.checked)} />
                  }
                  label="Utskrift"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Grid>
        <Grid item container flexDirection="column" gap={2}>
          <Grid item container spacing={5}>
            <Grid item md={4}>
              <FormControl fullWidth margin="dense">
                <Autocomplete
                  options={
                    endCustomer
                      ? [endCustomer]
                      : endCustomers.filter(
                          (endCustomer) =>
                            Number(endCustomer.SystemStatus) !== 3
                        )
                  }
                  size="small"
                  getOptionLabel={(option) =>
                    option
                      ? `${option.ExternalCustomerNr} - ${option.Title}`
                      : ""
                  }
                  value={
                    endCustomer
                      ? endCustomer
                      : endCustomers.find(
                          (ec) => ec.Id === invoiceData?.endCustomerId
                        ) || null
                  }
                  disabled={loading}
                  onChange={(_, newValue) => {
                    const val = {
                      target: {
                        name: "endCustomerId",
                        value: newValue ? newValue.Id : null,
                      },
                    };
                    handleInputChange(val);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Slutkund"
                      variant="outlined"
                    />
                  )}
                  fullWidth
                />
              </FormControl>
            </Grid>
            <Grid item md={4}>
              <CustomTextField
                handleInputChange={(e) => {
                  if (externalInvoiceNoErr) {
                    setExternalInvoiceNoErr("");
                  }
                  handleInputChange(e);
                }}
                name="invoiceNo"
                label="Externt fakturanr"
                disabled={externalCheckLoading}
                value={invoiceData.invoiceNo}
                error={!!externalInvoiceNoErr}
                helperText={externalInvoiceNoErr}
                onBlur={isAdmin ? asyncCheckExternalInvoiceNo : null}
                InputProps={{
                  endAdornment: externalCheckLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size="20px" />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <CustomTextField
                handleInputChange={handleInputChange}
                name="amount"
                label="Belopp"
                disabled={allowPrint}
                value={
                  allowPrint && lineCalcAmount
                    ? lineCalcAmount
                    : invoiceData.amount || ""
                }
              />
            </Grid>
          </Grid>
          <Grid item container spacing={5}>
            <Grid item md={4}>
              <FormControl fullWidth margin="dense" sx={{ textAlign: "left" }}>
                <SelectWithClear
                  label="Valutakod"
                  name="currencyCode"
                  value={invoiceData.currencyCode}
                  onChange={(e) => handleInputChange(e)}
                >
                  {CURRENCYCODE.map((obj) => (
                    <MenuItem key={obj.Value} value={obj.Value}>
                      {obj.Name}
                    </MenuItem>
                  ))}
                </SelectWithClear>
              </FormControl>
            </Grid>
            <Grid item md={4}>
              <FormControl fullWidth margin="dense" sx={{ textAlign: "left" }}>
                <SelectWithClear
                  label="Tjänst"
                  name="service"
                  value={invoiceData.service}
                  onChange={(e) => handleInputChange(e)}
                >
                  {DEFAULT_SERVICE?.map((obj) => (
                    <MenuItem key={obj.Value} value={obj.id}>
                      {obj.Name}
                    </MenuItem>
                  ))}
                </SelectWithClear>
              </FormControl>
            </Grid>
            <Grid item md={4}>
              <CustomTextField
                handleInputChange={handleInputChange}
                name="OCR"
                label="OCR"
                value={invoiceData.OCR}
              />
            </Grid>
          </Grid>
          <Grid item container spacing={5}>
            <Grid item md={4}>
              <CustomTextField
                handleInputChange={handleInputChange}
                name="ourReference"
                label="Vår referens"
                value={invoiceData.ourReference}
              />
            </Grid>
            <Grid item md={4}>
              <DateWithClear
                label="Faktura datum"
                value={dayjs(invoiceData.invoiceDate)}
                onChange={(value) =>
                  setInvoiceData((prevData) => ({
                    ...prevData,
                    invoiceDate: dayjs(value).format("YYYY-MM-DD"),
                  }))
                }
              />
            </Grid>
            <Grid item md={4}>
              <DateWithClear
                label="Förfallodatum"
                value={dayjs(invoiceData.dueDate)}
                onChange={(value) =>
                  setInvoiceData((prevData) => ({
                    ...prevData,
                    dueDate: dayjs(value).format("YYYY-MM-DD"),
                  }))
                }
              />
            </Grid>
          </Grid>
          <Collapse in={allowPrint}>
            <Grid item container spacing={5}>
              <Grid item md={4}>
                <CustomTextField
                  handleInputChange={handleInputChange}
                  name="externalReference"
                  label="Er referens"
                  value={invoiceData.externalReference}
                />
              </Grid>
              <Grid item md={4}>
                <CustomTextField
                  handleInputChange={handleInputChange}
                  name="orderNo"
                  label="Ordernummer"
                  value={invoiceData.orderNo}
                />
              </Grid>
              <Grid item md={4}>
                <CustomTextField
                  handleInputChange={handleInputChange}
                  name="gln"
                  label="GLN"
                  value={invoiceData.gln}
                />
              </Grid>
            </Grid>
            <Grid item container>
              <ArticlesTable rows={[]} editable setLines={setLines} />
            </Grid>
          </Collapse>
        </Grid>
        <Grid item container spacing={2} mt={3}>
          <Grid item md={6}>
            <Button
              sx={{
                backgroundColor: "transparent",
                outline: "1px solid #1976d2",
                color: "#1976d2",
              }}
              fullWidth
              onClick={handleClose}
            >
              Avbryt
            </Button>
          </Grid>
          <Grid item md={6}>
            <LoadingButton
              variant="contained"
              fullWidth
              onClick={handleCreate}
              loading={loading}
              disabled={
                !invoiceData.invoiceNo ||
                externalCheckLoading ||
                !!externalInvoiceNoErr ||
                !invoiceData.endCustomerId
              }
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
              }}
            >
              Skapa
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
}

const CustomTextField = ({
  name,
  label,
  value,
  handleInputChange,
  disabled = false,
  type = "string",
  ...props
}) => (
  <TextField
    name={name}
    label={label}
    value={value || ""}
    disabled={disabled}
    onChange={(e) => handleInputChange(e)}
    size="small"
    color="primary"
    fullWidth
    sx={{ maxWidth: 280 }}
    type={type}
    margin="dense"
    {...props}
  />
);
